import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { buildConstructionApiPayload, fetchConstructionById, updateConstruction } from '../api/postApi';
import { junctionBoxYnToDisplay } from '../utils/postForm';

const toDetailForm = (item) => ({
  installationYear: item.installationYear ?? '',
  businessName: item.businessName ?? '',
  managementNumber: item.managementNumber ?? '',
  possessor: item.possessor ?? '',
  possessorPhoneNumber: item.possessorPhoneNumber ?? '',
  address: item.address ?? '',
  detailAddress: item.detailAddress ?? '',
  installationCompany: item.installationCompany ?? '',
  contractorManager: item.contractorManager ?? '',
  contractorPhoneNumber: item.contractorPhoneNumber ?? '',
  moduleManufacturer: item.moduleManufacturer ?? '',
  moduleModelName: item.moduleModelName ?? '',
  moduleQuantity: item.moduleQuantity != null ? String(item.moduleQuantity) : '',
  inverterManufacturer: item.inverterManufacturer ?? '',
  inverterModelName: item.inverterModelName ?? '',
  ratedVoltage: item.ratedVoltage != null ? String(item.ratedVoltage) : '',
  capacity: item.capacity != null ? String(item.capacity) : '',
  inverterQuantity: item.inverterQuantity != null ? String(item.inverterQuantity) : '',
  inverterCapacity: item.inverterCapacity != null ? String(item.inverterCapacity) : '',
  junctionBoxYn: junctionBoxYnToDisplay(item.junctionBoxYn),
  note: item.note ?? '',
});

function PostInstallationDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const detail = await fetchConstructionById(id);
        setForm(toDetailForm(detail));
      } catch (error) {
        window.alert(`상세 조회 실패: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSave = async () => {
    if (!form || saving) return;
    const shouldSave = window.confirm('저장하시겠습니까?');
    if (!shouldSave) return;

    setSaving(true);
    try {
      await updateConstruction(id, buildConstructionApiPayload(form));
      window.alert('저장이 완료되었습니다.');
      navigate('/posts/installation');
    } catch (error) {
      window.alert(`저장 실패: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onMoveToAsCreate = () => {
    navigate('/posts/new', {
      state: {
        prefillConstruction: form,
      },
    });
  };

  if (loading || !form) {
    return <p className="page">불러오는 중...</p>;
  }

  return (
    <section className="page">
      <div className="page-header">
        <h2>시공정보 상세 조회 / 수정</h2>
        <button type="button" className="secondary" onClick={onMoveToAsCreate}>
          AS 작성
        </button>
      </div>

      <div className="form-sections">
        <section className="form-section">
          <h3>기본 정보</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="detail-installationYear">설치연도</label>
              <input id="detail-installationYear" name="installationYear" value={form.installationYear} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-businessName">사업명</label>
              <input id="detail-businessName" name="businessName" value={form.businessName} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-managementNumber">관리번호</label>
              <input id="detail-managementNumber" name="managementNumber" value={form.managementNumber} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-possessor">설비 소유자</label>
              <input id="detail-possessor" name="possessor" value={form.possessor} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-possessorPhoneNumber">설비 소유자 전화번호</label>
              <input id="detail-possessorPhoneNumber" name="possessorPhoneNumber" value={form.possessorPhoneNumber} onChange={onChange} />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h3>주소 정보</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="detail-address">주소</label>
              <input id="detail-address" name="address" value={form.address} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-detailAddress">상세 주소</label>
              <input id="detail-detailAddress" name="detailAddress" value={form.detailAddress} onChange={onChange} />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h3>시공 정보</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="detail-installationCompany">시공 업체</label>
              <input id="detail-installationCompany" name="installationCompany" value={form.installationCompany} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-contractorManager">시공 업체 담당자</label>
              <input id="detail-contractorManager" name="contractorManager" value={form.contractorManager} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-contractorPhoneNumber">시공 업체 담당자 전화번호</label>
              <input
                id="detail-contractorPhoneNumber"
                name="contractorPhoneNumber"
                value={form.contractorPhoneNumber}
                onChange={onChange}
              />
            </div>
            <div className="field">
              <label htmlFor="detail-junctionBoxYn">접속반 유무(Y/N)</label>
              <input id="detail-junctionBoxYn" name="junctionBoxYn" value={form.junctionBoxYn} onChange={onChange} />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h3>설비 정보</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="detail-capacity">설비 용량(kw)</label>
              <input id="detail-capacity" name="capacity" value={form.capacity} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-moduleManufacturer">모듈 제조사</label>
              <input id="detail-moduleManufacturer" name="moduleManufacturer" value={form.moduleManufacturer} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-moduleModelName">모듈 모델명</label>
              <input id="detail-moduleModelName" name="moduleModelName" value={form.moduleModelName} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-moduleQuantity">모듈 수량</label>
              <input id="detail-moduleQuantity" name="moduleQuantity" value={form.moduleQuantity} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-inverterManufacturer">인버터 제조사</label>
              <input id="detail-inverterManufacturer" name="inverterManufacturer" value={form.inverterManufacturer} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-inverterModelName">인버터 모델명</label>
              <input id="detail-inverterModelName" name="inverterModelName" value={form.inverterModelName} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-ratedVoltage">정격전압(V)</label>
              <input id="detail-ratedVoltage" name="ratedVoltage" value={form.ratedVoltage} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-inverterQuantity">인버터 수량</label>
              <input id="detail-inverterQuantity" name="inverterQuantity" value={form.inverterQuantity} onChange={onChange} />
            </div>
            <div className="field">
              <label htmlFor="detail-inverterCapacity">인버터 용량(kw)</label>
              <input id="detail-inverterCapacity" name="inverterCapacity" value={form.inverterCapacity} onChange={onChange} />
            </div>
            <div className="field field-wide">
              <label htmlFor="detail-note">기타사항</label>
              <textarea id="detail-note" name="note" rows={4} value={form.note} onChange={onChange} />
            </div>
          </div>
        </section>
      </div>

      <div className="button-row">
        <button type="button" className="secondary" onClick={() => navigate('/posts/installation')}>
          목록으로
        </button>
        <button type="button" onClick={onSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </section>
  );
}

export default PostInstallationDetailPage;
